/**
 *
 *  Add Card Modal
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 01/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";

// Components
import Button from "../Button/Button";
import CardNumberInputField from "./CardNumberInputField/CardNumberInputField";
import CardNumberExpiryField from "./CardNumberExpiryField/CardNumberExpiryField";
import CardCvcField from "./CardCvcField/CardCvcField";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

// Icons
import CloseIcon from "../../icons/CloseIcon";

// Images
import Visa from "../Checkout/Visa";

import mastercard from "../../images/payment-method-mastercard.png";
import mastercard2x from "../../images/payment-method-mastercard@2x.png";
import mastercard3x from "../../images/payment-method-mastercard@3x.png";

import amex from "../../images/payment-method-american-express.png";
import amex2x from "../../images/payment-method-american-express@2x.png";
import amex3x from "../../images/payment-method-american-express@3x.png";

const MasterCardImg = ({ className }) => (
  <img
    className={className}
    src={mastercard}
    srcSet={`${mastercard} 1x, ${mastercard2x} 2x, ${mastercard3x} 3x`}
    alt="Mastercard"
    width="40"
    height="40"
  />
);

const AmexImg = ({ className }) => (
  <img
    className={className}
    src={amex}
    srcSet={`${amex} 1x, ${amex2x} 2x, ${amex3x} 3x`}
    alt="Mastercard"
    width="40"
    height="40"
  />
);

class AddCardModal extends React.Component {
  static propTypes = {
    theme: PropTypes.object.isRequired,
    elements: PropTypes.object.isRequired,
    onCardChange: PropTypes.func.isRequired,
    onValidSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  static displayName = "AddCardModal";

  constructor(props) {
    super(props);

    this.closeModal = this.closeModal.bind(this);
    this.handleCardNumberChange = this.handleCardNumberChange.bind(this);
    this.handleCardExpiryChange = this.handleCardExpiryChange.bind(this);
    this.handleCardCvcChange = this.handleCardCvcChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.cardElementRef = React.createRef();

    this.state = {
      isModalOpen: false,
      isCopyTooltipShow: false,
      // canSend: false,
    };
  }

  openModal() {
    return new Promise((resolve) => {
      this.modalResolve = resolve;

      this.setState({
        isModalOpen: true,
      });
    });
  }

  closeModal() {
    this.setState({
      isModalOpen: false,
    });

    this.props.onClose();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.openModal();
    } else if (!this.props.isOpen && prevProps.isOpen) {
      this.closeModal();
    }
  }

  handleSubmit(e) {
    const cardElement = this.props.elements.getElement("cardNumber"); //(this.cardElementRef/*CardElement*/);

    e.preventDefault();

    this.props.onValidSubmit(cardElement);
  }

  handleCardNumberChange(e) {
    this.setState({ cardNumberComplete: e.complete, brand: e.brand });

    this.props.onCardChange(e);
  }

  handleCardExpiryChange(e) {
    this.setState({ cardExpiryComplete: e.complete });

    this.props.onCardChange(e);
  }

  handleCardCvcChange(e) {
    this.setState({ cardCvcComplete: e.complete });

    this.props.onCardChange(e);
  }

  render() {
    const defaultCustomStyle =
      clientWidth() < this.props.theme.responsive.breakpoints.sm
        ? Object.assign(
            {},
            { ...modalStyles.mobileDefault },
            {
              overlay: {
                ...modalStyles.mobileDefault.overlay,
                zIndex: 1060,
              },
            }
          )
        : Object.assign(
            {},
            { ...modalStyles.default },
            { overlay: { ...modalStyles.default.overlay, zIndex: 1060 } }
          );

    const canSend =
      this.state.cardNumberComplete &&
      this.state.cardExpiryComplete &&
      this.state.cardCvcComplete;

    return (
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={this.state.isModalOpen}
        onAfterOpen={this.afterOpenModal}
        onRequestClose={this.closeModal}
        style={defaultCustomStyle}
        contentLabel="Share Modal"
      >
        <button
          onClick={this.closeModal}
          className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        <div className="share-modal tbk-relative tbk-mb-3 tbk-rounded-lg tbk-bg-basic-white tbk-p-3 md:tbk-p-0">
          <h2 className="tbk-text-title-bold tbk-t-0 tbk-mb-4 tbk-text-primary">
            Add a new card
          </h2>

          <form onSubmit={this.handleSubmit}>
            <div className="">
              <div className="tbk-mb-3 tbk-flex tbk-items-end">
                <CardNumberInputField
                  elementRef={this.cardElementRef}
                  name="cardNumber"
                  className="tbk-w-full md:tbk-mr-2 md:tbk-w-72"
                  requiredMsg="This is required"
                  // onAfterTooltipHide={() => this.formRef.setFormPristine(true)}
                  onChange={this.handleCardNumberChange}
                  required
                />
                <div className="tbk-mb-0.5 tbk-hidden tbk-w-36 tbk-shrink-0 md:tbk-flex">
                  {this.state.brand && this.state.brand !== "unknown" ? (
                    this.state.brand === "visa" ? (
                      <Visa className="tbk-mr-1 tbk-h-5 tbk-w-5 tbk-shrink-0" />
                    ) : this.state.brand === "mastercard" ? (
                      <div className="tbk-mr-1 tbk-flex tbk-h-5 tbk-w-5 tbk-shrink-0 tbk-items-center tbk-rounded tbk-bg-grey-light">
                        <MasterCardImg className="tbk-h-4" />
                      </div>
                    ) : this.state.brand === "amex" ? (
                      <AmexImg className="tbk-mr-1 tbk-h-5 tbk-w-5 tbk-shrink-0 tbk-rounded" />
                    ) : null
                  ) : (
                    <>
                      <Visa className="tbk-mr-1 tbk-h-5 tbk-w-5 tbk-shrink-0" />
                      <div className="tbk-mr-1 tbk-flex tbk-h-5 tbk-w-5 tbk-shrink-0 tbk-items-center tbk-rounded tbk-bg-grey-light">
                        <MasterCardImg className="tbk-h-4" />
                      </div>
                      <AmexImg className="tbk-mr-1 tbk-h-5 tbk-w-5 tbk-shrink-0 tbk-rounded" />
                    </>
                  )}
                </div>
              </div>
              <div className="tbk-mb-3 tbk-flex">
                <div className="tbk-mr-3 tbk-w-full md:tbk-w-32">
                  <CardNumberExpiryField
                    name="cardExpiry"
                    className=""
                    requiredMsg="This is required"
                    // onAfterTooltipHide={() => this.formRef.setFormPristine(true)}
                    onChange={this.handleCardExpiryChange}
                    required
                  />
                </div>
                <div className="tbk-w-full md:tbk-w-32">
                  <CardCvcField
                    name="cardCvc"
                    className=""
                    requiredMsg="This is required"
                    // onAfterTooltipHide={() => this.formRef.setFormPristine(true)}
                    onChange={this.handleCardCvcChange}
                    required
                  />
                </div>
              </div>
            </div>
            <Button
              type="submit"
              className="tbk-mb-3 tbk-w-full"
              disabled={!canSend || this.props.isFetching}
              showLoader={this.props.isFetching}
            >
              Add card
            </Button>
          </form>
        </div>
      </Modal>
    );
  }
}

export default AddCardModal;
